<script setup>
import employmentStatuses from "@/config/lists/employmentStatuses";
import workSatisfactionOptions from "@/config/lists/workSatisfactionOptions";
import { useListStore, useLivelihoodQuestionnaireStore } from "@/stores";
import { computed, onMounted } from "vue";

const livelihoodQuestionnaireStore = useLivelihoodQuestionnaireStore();
const listStore = useListStore();

const keyword = computed({
  get: () => livelihoodQuestionnaireStore.filters.keyword,
  set: (value) => (livelihoodQuestionnaireStore.filters.keyword = value),
});

const advanced = computed({
  get: () => livelihoodQuestionnaireStore.filters.advanced,
  set: (value) => (livelihoodQuestionnaireStore.filters.advanced = value),
});

const appliedFilters = computed(() => {
  const adv = advanced.value;
  return Object.values(adv).filter((val) => {
    if (val == null) return false; // null or undefined
    if (Array.isArray(val)) return val.length > 0; // empty array
    if (typeof val === "string") return val.trim() !== ""; // empty string
    return true; // all other non-null, non-empty values
  }).length;
});

const iscoCodes = computed(() => listStore.iscoCodes);
const branches = computed(() => listStore.branches);
const courses = computed(() => listStore.courses);
const emplOfficers = computed(() => listStore.employabilityOfficers);
const professionalFields = computed(() => listStore.professionalFields);
const programs = computed(() => listStore.programs);

const jobPositions = computed(() => listStore.jobPositions);

const companiesSectors = computed(() => listStore.companiesSectors);

const handleResetBtnClick = () => {
  livelihoodQuestionnaireStore.resetAction();
};

const hideSimpleSearch = computed(() => {
  return false;
});

const variant = "employability";

const booleanOptions = [
  { text: "Yes", value: true },
  { text: "No", value: false },
];

const jobFoundMethods = [
  { name: "Route 1: I found the job myself", value: "ROUTE_1" },
  { name: "Route 2: Through Odyssea's network", value: "ROUTE_2" },
  { name: "Route 3: Through a job fair", value: "ROUTE_3" },
  { name: "Route 4: Through a referral", value: "ROUTE_4" },
  { name: "Other", value: "OTHER" },
];

const booleanNotSureOptions = [
  { name: "Yes", value: "YES" },
  { name: "No", value: "NO" },
  { name: "Not sure", value: "NOT_SURE" },
];

const typesOfEmployment = [
  { name: "Full time", value: "FULL_TIME" },
  { name: "Part time", value: "PART_TIME" },
  { name: "Irregular", value: "IRREGULAR" },
  { name: "Freelancer", value: "FREELANCER" },
  { name: "Internship", value: "INTERNSHIP" },
  { name: "No contract (Deprecated)", value: "NO_CONTRACT" },
  { name: "Seasonal (Deprecated)", value: "SEASONAL" },
  { name: "Contract (Deprecated)", value: "CONTRACT" },
  { name: "Other (Deprecated)", value: "OTHER" },
];

const durationsOfContracts = [
  { name: "Indefinite", value: "INDEFINITE" },
  {
    name: "1-month",
    value: "ONE_MONTH",
  },
  { name: "3-month", value: "THREE_MONTH" },
  { name: "6-month", value: "SIX_MONTH" },
  { name: "Other", value: "OTHER" },
];

const nextJobPriorities = [
  { name: "Salary", value: "SALARY" },
  { name: "Location", value: "LOCATION" },
  { name: "Stability", value: "STABILITY" },
  { name: "More suitable role", value: "MORE_SUITABLE_ROLE" },
  { name: "Working hours", value: "WORKING_HOURS" },
  { name: "Employment type", value: "EMPLOYMENT_TYPE" },
  { name: "Other", value: "OTHER" },
];

const restrictFutureDates = (val) => {
  return val <= new Date();
};

onMounted(async () => {
  await listStore.fetchCompaniesSectors();
  await listStore.fetchBranches();
  await listStore.fetchPrograms();
});
</script>

<template>
  <div v-if="!hideSimpleSearch">
    <v-text-field
      v-model="advanced.name"
      name="name"
      label="Search by beneficiary name"
    />
  </div>
  <v-expansion-panels class="mb-5">
    <v-expansion-panel :class="`advanced-filters-panel panel-${variant}`">
      <v-expansion-panel-title :class="`title-${variant}`">
        <v-icon icon="mdi-filter" /> <b>Advanced Filters</b>
        <div class="ml-2 text-caption font-weight-bold">
          ({{ appliedFilters }}
          <span v-if="appliedFilters == 1">filter</span>
          <span v-else>filters</span> applied)
        </div>
      </v-expansion-panel-title>
      <v-expansion-panel-text>
        <div class="d-flex ga-5 filter-row">
          <div class="flex-1-0-0">
            <v-text-field
              v-model="advanced.name"
              label="Beneficiary Name"
              clearable
              name="name"
            />
          </div>

          <div class="flex-1-0-0">
            <v-select
              v-model="advanced.jobFoundMethod"
              :items="jobFoundMethods"
              item-title="name"
              item-value="value"
              variant="outlined"
              label="How did you find the job?"
              clearable
            />
          </div>

          <div class="flex-1-0-0">
            <v-select
              v-model="advanced.didOdysseaSupport"
              :items="booleanOptions"
              item-title="text"
              item-value="value"
              variant="outlined"
            >
              <template #label>
                Did Odyssea support him/her in finding employment?
              </template>
            </v-select>
          </div>

          <div class="flex-1-0-0">
            <v-select
              v-model="advanced.branch"
              :items="branches"
              item-title="name"
              item-value="id"
              variant="outlined"
              label="Branch"
              clearable
            />
          </div>

          <div class="flex-1-0-0">
            <v-autocomplete
              v-model="advanced.intakeProjects"
              label="Odyssea Programs"
              :items="programs"
              variant="outlined"
              item-title="name"
              item-value="id"
              clearable
              multiple
              chips
              closable-chips
            />
          </div>

          <div class="flex-1-0-0">
            <v-text-field
              label="Employer name"
              v-model="advanced.employerName"
            ></v-text-field>
          </div>

          <div class="flex-1-0-0">
            <v-select
              :items="booleanNotSureOptions"
              v-model="advanced.partOfNetwork"
              item-title="name"
              item-value="value"
              variant="outlined"
              label="Is the company part of our network?"
            />
          </div>
        </div>
        <div class="d-flex ga-5 filter-row">
          <div class="flex-1-0-0">
            <v-select
              v-model="advanced.fieldOfEmployment"
              label="Field of employment"
              item-title="name"
              item-value="id"
              variant="outlined"
              :items="companiesSectors"
              :multiple="false"
              :chips="false"
            />
          </div>
          <div class="flex-1-0-0">
            <v-autocomplete
              v-model="advanced.jobTitleId"
              v-model:search-input="jobPositions"
              placeholder="Start typing to Search"
              item-title="name"
              item-value="id"
              :items="jobPositions"
              variant="outlined"
              hide-details="auto"
              label="Job Title"
            />
          </div>
          <div class="flex-1-0-0">
            <v-select
              v-model="advanced.typeOfEmployment"
              label="Type of employment"
              item-title="name"
              item-value="value"
              variant="outlined"
              :items="typesOfEmployment"
              :multiple="false"
              :chips="false"
            />
          </div>

          <div class="flex-1-0-0">
            <v-select
              v-model="advanced.durationOfContract"
              label="Duration of Contract"
              item-title="name"
              item-value="value"
              variant="outlined"
              :items="durationsOfContracts"
              :multiple="false"
              :chips="false"
            />
          </div>
        </div>

        <div class="d-flex ga-5 filter-row">
          <div class="flex-1-0-0">
            <v-range-slider
              v-model="advanced.netSalaryRange"
              :min="380"
              :max="3000"
              step="50"
              label="Net salary range (€)"
              thumb-label="always"
            />
          </div>
        </div>

        <div class="d-flex ga-5 filter-row">
          <div class="flex-1-0-0">
            <v-date-input
              v-model="advanced.dateDates"
              :allowed-dates="restrictFutureDates"
              label="Date"
              variant="outlined"
              prepend-icon=""
              multiple="range"
              placeholder="DD/MM/YYYY"
              prepend-inner-icon="$calendar"
              clearable
            />
          </div>
        </div>

        <div class="d-flex ga-5 filter-row">
          <div class="flex-1-0-0">
            <v-date-input
              v-model="advanced.startDates"
              :allowed-dates="restrictFutureDates"
              label="Job started"
              variant="outlined"
              prepend-icon=""
              multiple="range"
              placeholder="DD/MM/YYYY"
              prepend-inner-icon="$calendar"
              clearable
            />
          </div>
          <div class="flex-1-0-0">
            <v-date-input
              v-model="advanced.endDates"
              :allowed-dates="restrictFutureDates"
              label="Job ended"
              variant="outlined"
              prepend-icon=""
              multiple="range"
              placeholder="DD/MM/YYYY"
              prepend-inner-icon="$calendar"
              clearable
            />
          </div>
          <div class="flex-1-0-0">
            <v-select
              v-model="advanced.socialSecurity"
              :items="booleanOptions"
              item-title="text"
              item-value="value"
              variant="outlined"
              label="Social security?"
            />
          </div>
        </div>
        <div class="d-flex ga-5 mt-5">
          <div class="flex-1-0-0">
            <v-slider
              v-model="advanced.jobSatisfactionLevel"
              :min="1"
              :max="10"
              step="1"
              label="Job Satisfaction Level (1-10)"
              thumb-label="always"
              ticks="always"
              tick-size="4"
            ></v-slider>
          </div>
        </div>
        <div class="d-flex ga-5 mt-5">
          <div class="flex-1-0-0">
            <v-select
              :items="booleanNotSureOptions"
              v-model="advanced.helpsGrow"
              item-title="name"
              item-value="value"
              variant="outlined"
            >
              <template #label>
                Do you feel this job helps you grow professionally?
              </template>
            </v-select>
          </div>
          <div class="flex-1-0-0">
            <v-select
              :items="booleanOptions"
              v-model="advanced.feelSecureInJob"
              item-title="text"
              item-value="value"
              variant="outlined"
              label="Feel secure in job?"
            />
          </div>
        </div>
        <div class="d-flex ga-5 mt-5">
          <div class="flex-1-0-0">
            <v-slider
              v-model="advanced.getAlong"
              :min="1"
              :max="5"
              step="1"
              thumb-label="always"
              ticks="always"
              tick-size="4"
            >
              <template #label>
                How well do you get along <br />
                with your team or managers? (1: Not well - 5: Very well)
              </template>
            </v-slider>
          </div>
        </div>

        <div class="d-flex ga-5 mt-5">
          <div class="flex-1-0-0">
            <v-slider
              v-model="advanced.jobMatchSkills"
              :min="1"
              :max="5"
              step="1"
              thumb-label="always"
              ticks="always"
              tick-size="4"
              label="Job Match Skills?"
            />
          </div>
        </div>

        <div class="d-flex ga-5 filter-row">
          <div class="flex-1-0-0">
            <v-select
              v-model="advanced.recommendEmployer"
              :items="booleanNotSureOptions"
              item-title="name"
              item-value="value"
              variant="outlined"
              label="   Would you recommend this employer to other beneficiaries?"
            />
          </div>
          <div class="flex-1-0-0">
            <v-select
              :items="booleanNotSureOptions"
              v-model="advanced.stillLookingForJob"
              item-title="name"
              item-value="value"
              variant="outlined"
              label="Are the still looking for a job?"
            />
          </div>
          <div class="flex-1-0-0">
            <v-select
              :items="nextJobPriorities"
              v-model="advanced.newJobLookingFor"
              item-title="name"
              item-value="value"
              variant="outlined"
              multiple
              label="What are you looking for in your new job?"
            />
          </div>
        </div>

        <div class="d-flex ga-5 mt-5">
          <v-btn
            :color="variant"
            variant="outlined"
            @click="handleResetBtnClick"
          >
            Reset
          </v-btn>
        </div>
      </v-expansion-panel-text>
    </v-expansion-panel>
  </v-expansion-panels>
</template>

<style scoped>
.filter-row {
  flex-wrap: wrap !important;
}

@media (max-width: 599px) {
  .filter-row > div {
    flex: 0 0 100% !important;
  }
}

@media (min-width: 600px) and (max-width: 959px) {
  .filter-row > div {
    flex: 0 0 calc(50% - 10px) !important;
  }
}
</style>
